import type { SmallImageCardProps } from '@stereoagency/ui';
import { TaxonomyType, defaultLanguage } from '@stereoagency/constants';
import { article as articleSchema } from '@stereoagency/sanity-schema';
import { createDocumentURL, dateFormatterFactory, findInKeyArray, sanityImageAsProps } from '@/utils';
import { SimpleBlockContent } from '@/components/sanity/client';
import { SpecialPagesContextValue } from '@/components/context';

const articlesQuery = articleSchema.schema.builder
  .pick(['datetime', 'slug', 'title', 'introduction', 'thumbnail'])
  .map((resolver) => ({
    taxonomies: resolver.taxonomies.resolveIn(['slug', 'name', 'type']).use()
  }))
  .use();

export default function articleAsProps(
  article: (typeof articlesQuery)[1][number],
  lang: string,
  config: SpecialPagesContextValue
): SmallImageCardProps | null {
  const dateFormatter = dateFormatterFactory(lang, {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric'
  });

  const title = article.title
    ? findInKeyArray(article.title, [lang, defaultLanguage.id])
    : null;

  const intro = article.introduction
    ? findInKeyArray(article.introduction, [lang, defaultLanguage.id])
    : null;

  if (
    !article.slug?.current ||
    !article.thumbnail?.asset._ref ||
    !title?.value ||
    !article.datetime
  ) {
    return null;
  }

  const category = article.taxonomies?.find(
    (tax) => tax.type === TaxonomyType.NEWS_CATEGORY
  );
  const categoryName = category?.name
    ? findInKeyArray(category.name, [lang, defaultLanguage.id])
    : null;
  let categoryUrl = config.pages.news_listing_page?.href
    ? config.pages.news_listing_page?.href
    : null;
  if (category?.slug?.current && categoryUrl) {
    categoryUrl = `${categoryUrl}?filters=${encodeURIComponent(category.slug.current)}`;
  }

  return {
    label: dateFormatter.format(new Date(article.datetime)),
    title: title.value,
    description: intro?.value ? (
      <SimpleBlockContent value={intro.value} />
    ) : null,
    image: sanityImageAsProps(article.thumbnail),
    tag:
      categoryName?.value && categoryUrl
        ? {
            href: categoryUrl,
            label: categoryName.value
          }
        : undefined,
    action: {
      href: createDocumentURL(lang, 'article', article.slug.current)
    }
  };
}
